import React, { useEffect, useState } from "react";
import { Container, Row, Col, Accordion } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLangPath } from "./hooks/useLangPath";
import Seo from "./Seo";

const homeSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "A.I. Trading Predictions",
    url: "https://aitradingpredictions.com",
    description: "Daily trading predictions for Forex, metals, indices, cryptocurrencies and commodities, powered by AI news sentiment analysis.",
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "A.I. Trading Predictions",
    url: "https://aitradingpredictions.com",
  },
];

function NewsTicker() {
  const { t } = useTranslation();
  const [headlines, setHeadlines] = useState([]);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    fetch("/data/top_headlines.json")
      .then(r => r.ok ? r.json() : null).catch(() => null)
      .then(d => { if (d?.headlines?.length) setHeadlines(d.headlines); });
  }, []);
  if (!headlines.length) return null;
  const items = [...headlines, ...headlines];
  return (
    <div style={tickerStyles.wrap}
         onMouseEnter={() => setPaused(true)}
         onMouseLeave={() => setPaused(false)}>
      <span style={tickerStyles.label}>{t("home.newsTickerLabel")}</span>
      <div style={tickerStyles.track}>
        <div style={{ ...tickerStyles.inner, animationPlayState: paused ? "paused" : "running" }}>
          {items.map((h, i) => (
            <span key={i} style={tickerStyles.item}>
              {h.title}
              <span style={tickerStyles.dot}>&nbsp;&nbsp;•&nbsp;&nbsp;</span>
            </span>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes ticker-scroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}

const tickerStyles = {
  wrap:  { display: "flex", alignItems: "center", background: "#16213e", borderTop: "1px solid #0f3460", padding: "8px 0", overflow: "hidden", cursor: "default" },
  label: { flexShrink: 0, background: "#0f3460", color: "#a8d8f0", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", padding: "4px 14px", marginRight: 16, whiteSpace: "nowrap", alignSelf: "stretch", display: "flex", alignItems: "center" },
  track: { overflow: "hidden", flex: 1 },
  inner: { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", animation: "ticker-scroll 200s linear infinite" },
  item:  { color: "#c9d6df", fontSize: 13 },
  dot:   { color: "#4a6fa5", fontWeight: 700 },
};

function Home() {
  const { t } = useTranslation();
  const langPath = useLangPath();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.scrollTo) {
      const el = document.getElementById(location.state.scrollTo);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  }, [location.state]);

  const sentimentColumns = [
    {
      key: "forex",
      labelKey: "sectors.forex",
      items: [
        { label: "EUR/USD", badge: "€$", path: "/forex-majors/eurusd-ai-trading-predictions" },
        { label: "GBP/USD", badge: "£$", path: "/forex-majors/gbpusd-ai-trading-predictions" },
        { label: "USD/JPY", badge: "$¥", path: "/forex-majors/usdjpy-ai-trading-predictions" },
        { label: "USD/CAD", badge: "$C", path: "/forex-majors/usdcad-ai-trading-predictions" },
        { label: "AUD/USD", badge: "A$", path: "/forex-majors/audusd-ai-trading-predictions" },
      ],
    },
    {
      key: "metals",
      labelKey: "sectors.metals",
      items: [
        { label: "Gold",   badge: "Au", path: "/metals/gold-ai-trading-predictions" },
        { label: "Silver", badge: "Ag", path: "/metals/silver-ai-trading-predictions" },
        { label: "Copper", badge: "Cu", path: "/metals/copper-ai-trading-predictions" },
      ],
    },
    {
      key: "indices",
      labelKey: "sectors.indices",
      items: [
        { label: "NAS100", icon: "bars", path: "/indices/nas100-ai-trading-predictions" },
        { label: "US500",  icon: "bars", path: "/indices/us500-ai-trading-predictions" },
        { label: "US30",   icon: "bars", path: "/indices/us30-ai-trading-predictions" },
        { label: "UK100",  icon: "bars", path: "/indices/uk100-ai-trading-predictions" },
      ],
    },
    {
      key: "crypto",
      labelKey: "sectors.crypto",
      items: [
        { label: "Bitcoin",      badge: "₿", path: "/cryptocurrencies/bitcoin-ai-trading-predictions" },
        { label: "Ethereum",     badge: "Ξ", path: "/cryptocurrencies/ethereum-ai-trading-predictions" },
        { label: "Ripple",       badge: "XRP", path: "/cryptocurrencies/ripple-ai-trading-predictions" },
        { label: "Solana",       badge: "SOL", path: "/cryptocurrencies/solana-ai-trading-predictions" },
        { label: "Litecoin",     badge: "Ł", path: "/cryptocurrencies/litecoin-ai-trading-predictions" },
        { label: "Bitcoin Cash", badge: "BCH", path: "/cryptocurrencies/bitcoin-cash-ai-trading-predictions" },
      ],
    },
    {
      key: "commodities",
      labelKey: "sectors.commodities",
      items: [
        { label: "Brent Oil",   icon: "drop", path: "/commodities/brent-crude-oil-ai-trading-predictions" },
        { label: "Natural Gas", icon: "flame", path: "/commodities/natural-gas-ai-trading-predictions" },
        { label: "Cocoa",       badge: "CC", path: "/commodities/cocoa-ai-trading-predictions" },
        { label: "Sugar",       badge: "SB", path: "/commodities/sugar-ai-trading-predictions" },
      ],
    },
  ];

  const faqs = Array.from({ length: 8 }, (_, i) => ({
    q: t(`home.faq_q${i + 1}`),
    a: t(`home.faq_a${i + 1}`),
  }));

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  return (
    <div>
      <Seo
        title="A.I. Trading Predictions — Sentiment-Based Market Forecasts"
        description="Daily AI trading predictions for Forex pairs, gold, silver, stock indices, cryptocurrencies and commodities. Forecasts powered by real-time news sentiment analysis."
        canonical="/"
        schema={[faqSchema, ...homeSchema]}
      />

      {/* Hero */}
      <div style={styles.hero}>
        <Container>
          <h1 style={styles.heroTitle}>{t("home.heroTitle")}</h1>
          <p style={styles.heroSubtitle}>{t("home.heroSubtitle")}</p>
          <p style={styles.heroBody}>{t("home.heroBody")}</p>
        </Container>
      </div>

      <NewsTicker />

      {/* How it works */}
      <div style={styles.section}>
        <Container>
          <h2 style={styles.sectionTitle}>{t("home.howItWorksTitle")}</h2>
          <Row className="text-center g-4">
            {[
              { step: "1", headingKey: "home.step1Heading", textKey: "home.step1Text" },
              { step: "2", headingKey: "home.step2Heading", textKey: "home.step2Text" },
              { step: "3", headingKey: "home.step3Heading", textKey: "home.step3Text" },
            ].map(({ step, headingKey, textKey }) => (
              <Col md={4} key={step}>
                <div style={styles.stepCircle}>{step}</div>
                <h5 style={{ fontWeight: 600, marginTop: 12 }}>{t(headingKey)}</h5>
                <p style={{ color: "var(--text-secondary)", fontSize: 15 }}>{t(textKey)}</p>
              </Col>
            ))}
          </Row>
        </Container>
      </div>

      {/* News Sentiment */}
      <div id="news-sentiment" style={{ ...styles.section, backgroundColor: "var(--surface-2)" }}>
        <Container>
          <h2 style={styles.sectionTitle}>{t("home.newsSentimentTitle")}</h2>
          <p style={styles.sectionSubtitle}>{t("home.newsSentimentSubtitle")}</p>
          <Row>
            {sentimentColumns.map(({ key, labelKey, items }) => (
              <Col key={key} xs={12} md style={{ marginBottom: 32 }}>
                <div style={styles.colHeader}>{t(labelKey)}</div>
                <ul style={styles.colList} className={"sym-list sym-" + key}>
                  {items.map(({ label: lbl, path, badge, icon }) => (
                    <li key={path}>
                      {/* A real anchor whose text is the instrument name — the
                          badge and chevron are decoration, hidden from crawlers
                          and screen readers alike. */}
                      <Link to={langPath(path)} className="sym-btn">
                        <span className="sym-badge" aria-hidden="true">
                          {icon === "bars" && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <rect x="3" y="12" width="5" height="9" rx="1" />
                              <rect x="10" y="6" width="5" height="15" rx="1" />
                              <rect x="17" y="9" width="5" height="12" rx="1" />
                            </svg>
                          )}
                          {icon === "drop" && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M12 2c4 5.5 7 9.2 7 13a7 7 0 1 1-14 0c0-3.8 3-7.5 7-13z" />
                            </svg>
                          )}
                          {icon === "flame" && (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                              <path d="M12 2s5 4.5 5 9a5 5 0 0 1-10 0c0-1.5.5-2.9 1.3-4.2C9 8.4 10.5 9 11 10c1.2-2.2.4-5.5 1-8z" />
                            </svg>
                          )}
                          {!icon && badge}
                        </span>
                        <span className="sym-label">{lbl}</span>
                        <span className="sym-arrow" aria-hidden="true">{"→"}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </Col>
            ))}
          </Row>
          <style>{`
            .sym-list { display: flex; flex-direction: column; gap: 8px; padding: 0; margin: 0; list-style: none; }

            /* One accent hue per category; everything else derives from it. */
            .sym-forex       { --sym-accent: 99, 102, 241; }
            .sym-metals      { --sym-accent: 245, 158, 11; }
            .sym-indices     { --sym-accent: 14, 165, 233; }
            .sym-crypto      { --sym-accent: 168, 85, 247; }
            .sym-commodities { --sym-accent: 16, 185, 129; }

            .sym-btn {
              display: flex; align-items: center; gap: 10px;
              padding: 9px 12px;
              border: 1px solid var(--surface-border, #e8ebef);
              border-radius: 10px;
              background: var(--surface, #fff);
              color: var(--text-strong, #374151);
              font-size: 14.5px; font-weight: 600;
              text-decoration: none;
              transition: border-color .16s ease, box-shadow .16s ease,
                          transform .16s ease;
            }
            .sym-badge {
              flex: 0 0 30px; height: 30px;
              display: inline-flex; align-items: center; justify-content: center;
              border-radius: 8px;
              font-size: 11px; font-weight: 700; letter-spacing: .02em;
              color: rgb(var(--sym-accent));
              background: rgba(var(--sym-accent), .12);
              transition: background .16s ease, color .16s ease;
            }
            .sym-label { flex: 1 1 auto; text-align: left; }
            .sym-arrow {
              opacity: 0; transform: translateX(-4px);
              color: rgb(var(--sym-accent));
              transition: opacity .16s ease, transform .16s ease;
              font-size: 14px;
            }
            .sym-btn:hover, .sym-btn:focus-visible {
              border-color: rgba(var(--sym-accent), .55);
              box-shadow: 0 0 0 1px rgba(var(--sym-accent), .25),
                          0 4px 18px rgba(var(--sym-accent), .22);
              transform: translateY(-1px);
              color: var(--text-strong, #374151);
            }
            .sym-btn:hover .sym-badge {
              background: rgb(var(--sym-accent));
              color: #fff;
            }
            .sym-btn:hover .sym-arrow { opacity: 1; transform: translateX(0); }
            .sym-btn:focus-visible { outline: 2px solid rgb(var(--sym-accent)); outline-offset: 1px; }
            @media (prefers-reduced-motion: reduce) {
              .sym-btn, .sym-badge, .sym-arrow { transition: none; }
              .sym-btn:hover { transform: none; }
            }
          `}</style>
        </Container>
      </div>

      {/* SEO text + FAQ */}
      <div style={{ ...styles.section, paddingBottom: 80 }}>
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <h2 style={styles.sectionTitle}>{t("home.seoTitle")}</h2>
              <p style={{ color: "var(--text-body)", lineHeight: 1.8 }}>{t("home.seoPara1")}</p>
              <p style={{ color: "var(--text-body)", lineHeight: 1.8 }}>{t("home.seoPara2")}</p>

              <Accordion flush className="mt-4">
                {faqs.map(({ q, a }, i) => (
                  <Accordion.Item key={i} eventKey={String(i)} style={styles.faqItem}>
                    <Accordion.Header>{q}</Accordion.Header>
                    <Accordion.Body style={styles.faqBody}>{a}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </div>
    </div>
  );
}

const styles = {
  hero: {
    background: "linear-gradient(135deg, #1a1a2e 0%, #16213e 60%, #0f3460 100%)",
    color: "#fff",
    padding: "80px 0 60px",
    textAlign: "center",
  },
  heroTitle:    { fontSize: "clamp(2rem, 5vw, 3.2rem)", fontWeight: 700, marginBottom: 16, letterSpacing: "-0.5px" },
  heroSubtitle: { fontSize: "clamp(1rem, 2.5vw, 1.3rem)", fontWeight: 400, color: "#a8d8f0", marginBottom: 24 },
  heroBody:     { fontSize: 16, color: "#c9d6df", maxWidth: 720, margin: "0 auto", lineHeight: 1.8 },
  section:      { padding: "60px 0" },
  sectionTitle: { textAlign: "center", fontWeight: 700, marginBottom: 12, fontSize: "clamp(1.4rem, 3vw, 2rem)" },
  sectionSubtitle: { textAlign: "center", color: "var(--text-secondary)", marginBottom: 36, fontSize: 16 },
  stepCircle: {
    width: 56, height: 56, borderRadius: "50%",
    background: "linear-gradient(135deg, #0f3460, #16213e)",
    color: "#fff", fontSize: 22, fontWeight: 700,
    display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto",
  },
  card:     { border: "none", borderRadius: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)", cursor: "pointer" },
  cardBody: { padding: "28px 24px" },
  cardIcon:  { fontSize: 32, marginBottom: 12 },
  cardTitle: { fontWeight: 600, color: "var(--heading)", marginBottom: 6 },
  cardText:  { color: "var(--text-secondary)", fontSize: 14, marginBottom: 0 },
  faqItem:  { borderBottom: "1px solid var(--line-strong)", backgroundColor: "transparent" },
  faqBody:  { color: "var(--text-body)", lineHeight: 1.8, fontSize: 15 },
  colHeader: { fontWeight: 700, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--heading)", borderBottom: "2px solid var(--accent-line)", paddingBottom: 8, marginBottom: 12 },
  colList:   { listStyle: "none", padding: 0, margin: 0 },
};

export default Home;
